import {useWindowDimensions} from 'react-native';

/**
 * Width (in dp) at/above which the management module switches from the phone
 * single-column layout to the tablet two-column layout. Matches the app-wide
 * phone/tablet convention already used elsewhere (BigSaleScreen,
 * PendingOrdersModal, HomeScreen route picker).
 * @type {number}
 */
export const MGMT_TABLET_BREAKPOINT = 768;

/**
 * Maximum readable content width for the management module. On very wide screens
 * (large tablets in landscape, web / desktop) the content column is capped at
 * this width and centred so cards never stretch awkwardly edge-to-edge. On
 * phones the real width is always smaller, so the cap is a no-op there.
 * @type {number}
 */
export const MGMT_MAX_CONTENT_WIDTH = 1180;

/** Horizontal gap between the two columns on tablet. @type {number} */
const MGMT_GRID_GAP = 14;

/**
 * Live, orientation-aware layout hint for the management module.
 *
 * Drives the responsive behaviour of every management screen: two columns with
 * centred, width-capped content on tablets / wide screens, and the normal
 * single stacked column on phones. Recomputes on rotation / split-view resize
 * because it reads {@link useWindowDimensions} rather than the static
 * `Dimensions.get('window')` snapshot.
 *
 * @returns {{width: number, height: number, isWide: boolean, isPhone: boolean,
 *   columns: number, gap: number, maxContentWidth: number}} Layout descriptor
 *   consumed by ManagementScreen, ManagementGrid and the individual screens.
 */
export const useManagementLayout = () => {
    const {width, height} = useWindowDimensions();
    const isWide = width >= MGMT_TABLET_BREAKPOINT;

    return {
        width,
        height,
        isWide,
        isPhone: !isWide,
        columns: isWide ? 2 : 1,
        gap: MGMT_GRID_GAP,
        maxContentWidth: MGMT_MAX_CONTENT_WIDTH,
    };
};

/**
 * Round-robin an ordered list of items into N balanced columns.
 *
 * Used by the detail-page two-column layout ({@link DetailColumns}) to spread a
 * tall vertical stack of independent blocks (cards / sections) across columns so
 * a long record reads without endless scrolling on tablets. Round-robin (item i
 * → column `i % columns`) keeps the two columns close in length when blocks have
 * similar heights, without needing to measure them. Pure + side-effect free so
 * it can be unit-tested in isolation.
 *
 * @template T
 * @param {T[]} items - Ordered blocks to distribute (falsy entries preserved as-is).
 * @param {number} [columns=2] - Number of target columns (clamped to >= 1).
 * @returns {T[][]} An array of `columns` arrays; column `c` holds every item whose
 *   index satisfies `index % columns === c`.
 */
export const splitColumns = (items = [], columns = 2) => {
    const count = Math.max(1, Math.floor(columns) || 1);
    const cols = Array.from({length: count}, () => []);
    (Array.isArray(items) ? items : []).forEach((item, index) => {
        cols[index % count].push(item);
    });

    return cols;
};

// Every bottom bar (the mode tab bars and the module bar) floats ABOVE the screen
// (`position: 'absolute'`, lifted 10 px + the bottom inset), so the scene runs
// underneath it. These mirror that geometry for the `floating` slot.
const FLOATING_BAR_OFFSET = 10;
const MODULE_BAR_HEIGHT = 62;

/**
 * Bottom clearance for the `floating` slot so a FAB sits above whichever floating
 * bar covers the screen — the module bar, the mode's tab bar (height read from
 * `BottomTabBarHeightContext`, undefined outside a tab navigator) or neither.
 *
 * @param {{hasModuleBar: boolean, tabBarHeight?: number, bottomInset: number}} params
 * @returns {number} px to keep free at the bottom of the screen.
 */
export const getFloatingClearance = ({hasModuleBar, tabBarHeight, bottomInset}) => {
    const inset = Number(bottomInset) || 0;
    if (hasModuleBar) {
        return MODULE_BAR_HEIGHT + FLOATING_BAR_OFFSET + inset;
    }
    if (Number(tabBarHeight) > 0) {
        return Number(tabBarHeight) + FLOATING_BAR_OFFSET + inset;
    }

    return inset;
};
